import React, { useState, useEffect, Fragment } from 'react';
import { useShowContext, SimpleShowLayout, Show, useTranslate, Loading, Error, useNotify } from 'react-admin';
import { Grid, Typography, Container, Skeleton, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Autocomplete } from '@mui/material';
import { CardCommon } from '../../../uiElements';
import { FormDivider } from 'src/components/uiElements/fields/Divider';
import { ShowListElement } from 'src/components/uiElements/CRUD/ShowListElement';
import { CardCrudBottomButtons } from 'src/components/uiElements/CRUD/CardCrudBottomButtons';
import { isUndefined } from 'src/helper/isUndefined';
import { paymentServices } from 'src/services/payment/Payment.service';
import { billingFormServices } from 'src/services/payment/billingForm/billingForm.service';
import { hasPermissionFunction } from 'src/helper/HasPermission'
import HistoryBaseDialog from './logic/HistoryBaseDialog';
import dayjs from 'dayjs';

const ShowWrapper = ({ children }) => <Container maxWidth='false'> {children}</Container>
const ShowContent = () => {
  const translate = useTranslate()
  const notify = useNotify()
  const [paymentTypeList, setPaymentTypeList] = useState([])
  const [loadingPaymentTypeList, setLoadingPaymentTypeList] = useState(false)
  const [paymentMethodList, setPaymentMethodList] = useState([])
  const [loadingPaymentMethodList, setLoadingPaymentMethodList] = useState(false)

  // Currency x Base
  const [responseCurrencyBase, setResponseCurrencyBase] = useState(null);
  const [openHistoryBase, setOpenHistoryBase] = useState(false);
  const [isLoadingCurrencyBase, setIsLoadingCurrencyBase] = useState(false);

  const {
    error, // error returned by dataProvider when it failed to fetch the record. Useful if you want to adapt the view instead of just showing a notification using the `onError` side effect.
    isLoading, // boolean that is true until the record is available for the first time
    record, // record fetched via dataProvider.getOne() based on the id from the location
  } = useShowContext()
  const getPaymentTypeService = async () => {
    setLoadingPaymentTypeList(true)
    try {
      const allPaymentType = await paymentServices.getAllPaymentTypes()
      if (allPaymentType) {
        setPaymentTypeList(allPaymentType)
        setLoadingPaymentTypeList(false)
        return
      }
      notify(`applications not found`, { type: 'error' })
      setLoadingPaymentTypeList(false)
    } catch (error) {
      notify(`No se encontraron tipos de pagos`, { type: 'error' })
      setLoadingPaymentTypeList(false)
    }
  }

  // Begin Get Currency Base
  const getCurrencyBase = async (workUnitHash) => {
    setIsLoadingCurrencyBase(true);
    try {
      let query = `work_unit_hash=${workUnitHash}`;
      const response = await billingFormServices.getCurrencyBaseRelations(query);
      setResponseCurrencyBase(response);
    } catch (error) {
      setResponseCurrencyBase([]);
    } finally {
      setIsLoadingCurrencyBase(false);
    }
  };

  useEffect(() => {
    if (record?.hash_work_unit) {
      getCurrencyBase(record.hash_work_unit);
    }
  }, [record]);

  const getPaymentMethodService = async () => {
    setLoadingPaymentMethodList(true)
    try {
      const allPaymentMethod = await paymentServices.getAllPaymentMethods()
      if (allPaymentMethod) {
        allPaymentMethod.forEach(
          (elem) =>
            (elem.label = `${elem.financial_entity_relation.long_name} - ${elem.card_relation.long_name}`)
        )
        setPaymentMethodList(allPaymentMethod)
        setLoadingPaymentMethodList(false)
        return
      }
      notify(`applications not found`, { type: 'error' })
      setLoadingPaymentMethodList(false)
    } catch (error) {
      notify(`please, refresh page`, { type: 'error' })
      setLoadingPaymentMethodList(false)
    }
  }

  useEffect(() => {
    getPaymentTypeService()
    getPaymentMethodService()
  }, [])

  if (isLoading) {
    return <Loading />
  }

  if (error) {
    return <Error />
  }

  const matchIdwithOption = (list_ids, options) => {
    if (
      list_ids &&
      Array.isArray(list_ids) &&
      list_ids.length > 0 &&
      options &&
      Array.isArray(options) &&
      options.length > 0
    ) {
      return options.filter((option) => list_ids.includes(option.id))
    }
    return []
  }

  const handleOpenHistoryBase = () => setOpenHistoryBase(true);
  
  const handleCloseHistoryBase = () => setOpenHistoryBase(false);

  return (
    <>
      <SimpleShowLayout>
        <CardCommon title={translate('ra.entity.work-unit')} actions={<CardCrudBottomButtons btnEdit={true} />}>
          <Grid container spacing={3}>
            <ShowListElement label={'ID'} value={record.id} />
            <ShowListElement label={translate('ra.field.long_name')} value={record.long_name} />
            <ShowListElement label={translate('ra.field.short_name')} value={record.short_name} />
            <ShowListElement label={translate('ra.field.description')} value={record.description} />
            <ShowListElement label={translate('ra.field.logo')} value={record?.logo} type='image' />
            <ShowListElement
              label={translate('ra.field.parent_workunit')}
              value={record.parent_relation?.long_name}
            />

            <ShowListElement
              label={translate('ra.field.payment_types')}
              type='array'
              value={
                loadingPaymentTypeList
                  ? [{ long_name: 'Loading...' }]
                  : matchIdwithOption(record.payment_type, paymentTypeList)
              }
              optionValue='long_name'
            />
            <ShowListElement
              label={translate('ra.field.payment_methods')}
              type='array'
              value={
                loadingPaymentMethodList
                  ? [{ label: 'Loading...' }]
                  : matchIdwithOption(record.payment_method, paymentMethodList)
              }
              optionValue='label'
            />
            <ShowListElement
              label={translate('ra.field.numbering_client')}
              value={record.client_numbering_relation?.long_name}
            />
            <ShowListElement
              label={translate('ra.field.numbering_reservation')}
              value={record.reservation_numbering_relation?.long_name}
            />
            <ShowListElement label={translate('ra.field.is_active')} value={record.is_active} type='boolean' />
            <ShowListElement label={translate('ra.field.token')} value={record.token} />
            <ShowListElement label={translate('ra.field.hash')} value={record.hash_work_unit} />
            <ShowListElement label={translate('ra.field.url')} value={isUndefined(record?.url)} />
            {/* <ShowListElement
            label={translate('ra.field.config')}
            value={isUndefined(record.config)}
            type={'json'}
          /> */}

            {/* begin Currency Base */}
            {hasPermissionFunction('billing', 'config') && (
              <>
                <FormDivider show={true} text={translate('ra.field.base')} />
                <Fragment>
                  <Grid container justifyContent="flex-end" style={{ marginBottom: 15 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleOpenHistoryBase}
                      style={{
                        fontWeight: 'bold',
                        borderWidth: 2,
                        borderRadius: 8,
                        transition: 'box-shadow 0.2s ease-in-out',
                      }}
                    >
                      {translate('ra.action.downloadHistory')}
                    </Button>
                  </Grid>
                  {!isLoadingCurrencyBase ?
                    <Fragment>
                      {responseCurrencyBase?.data ? (
                        <Fragment>
                          {responseCurrencyBase?.data.map((field, index) => (
                            <Grid container styñe={{ marginBottom: 35 }} >
                              <ShowListElement
                                label={translate('ra.field.currency_from')}
                                value={field.currency_from_relation.short_name}
                              />
                              <ShowListElement
                                label={translate('ra.field.currency_to')}
                                value={field.currency_to_relation.short_name}
                              />

                              <ShowListElement label={translate('ra.field.value')} value={field.value} />
                              <Grid item style={{ paddingLeft: 15, top: '-10px', position: 'relative' }}>
                                <div><Typography className='valueText'
                                  component='div' style={{ fontWeight: 'bold' }}>Bases:</Typography></div>
                                {field.bases.map((element, index) =>
                                  <Chip size="small" label={element.name} variant="outlined" color="primary" />
                                )}
                              </Grid>
                              <Grid item xs={12}>
                                <ShowListElement
                                  label={translate('ra.field.isInherited')}
                                  value={field.is_inherited ? 'Si' : 'No'}
                                />
                              </Grid>
                            </Grid>

                          ))}
                        </Fragment>
                      ) : (
                        <Grid item>
                          <Typography className='valueText'
                            component='div'>{translate('ra.message.no_registry')}</Typography>
                        </Grid>
                      )}
                    </Fragment>
                    :
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4} lg={4} xl={4}><Skeleton variant="rectangular" width="100%" height={30} /></Grid>
                      <Grid item xs={12} md={4} lg={4} xl={4}><Skeleton variant="rectangular" width="100%" height={30} /></Grid>
                      <Grid item xs={12} md={4} lg={4} xl={4}><Skeleton variant="rectangular" width="100%" height={30} /></Grid>
                    </Grid>
                  }
                </Fragment>
              </>
            )}
            {/* end Currency Base */}

            <FormDivider show={true} text={translate('ra.field.currency')} />
            {record.currency_relation?.length > 0 ? (
              record.currency_relation?.map(
                (field, index) =>
                  !field.is_reverse && (
                    <Grid container key={index}>
                      <ShowListElement
                        label={translate('ra.field.currency_from')}
                        value={field.currency_relation?.long_name}
                      />
                      <ShowListElement
                        label={translate('ra.field.currency_to')}
                        value={field.currency_to_relation?.long_name}
                      />
                      <ShowListElement label={translate('ra.field.value')} value={field.value} />
                      <ShowListElement label={translate('ra.field.billable_exchange_rate')} value={field.billable_exchange_rate} />

                    </Grid>
                  )
              )
            ) : (
              <Grid item>
                <Typography variant='button'>{translate('ra.message.no_registry')}</Typography>
              </Grid>
            )}

            <FormDivider show={true} text={translate('ra.field.currency_inheritance')} />
            {record.currency_inheritance?.length > 0 ? (
              record.currency_inheritance?.map(
                (field, index) =>
                  !field.is_reverse && (
                    <Grid container key={index}>
                      <ShowListElement
                        label={translate('ra.field.currency_from')}
                        value={field.currency_relation?.long_name}
                      />
                      <ShowListElement
                        label={translate('ra.field.currency_to')}
                        value={field.currency_to_relation?.long_name}
                      />
                      <ShowListElement label={translate('ra.field.value')} value={field.value} />
                    </Grid>
                  )
              )
            ) : (
              <Grid item>
                <Typography variant='button'>{translate('ra.message.no_registry')}</Typography>
              </Grid>
            )}

            <FormDivider show={true} text={translate('ra.field.products')} />
            {record.product_by_work_unit_relation?.length ? (
              <>
                {record.product_by_work_unit_relation?.map((field, index) => (
                  <Grid container key={index}>
                    <ShowListElement
                      label={translate('ra.field.long_name')}
                      value={field.product_relation.long_name}
                    />
                    <ShowListElement
                      label={translate('ra.field.short_name')}
                      value={field.product_relation.short_name}
                    />
                    <ShowListElement
                      label={translate('ra.field.tma_code')}
                      value={field.product_relation.tma_code}
                    />
                    <ShowListElement label={translate('ra.field.token')} value={field.token_id} />
                    <ShowListElement
                      label={translate('ra.field.is_offline')}
                      value={field.product_relation.is_offline}
                      type='boolean'
                    />
                  </Grid>
                ))}
              </>
            ) : (
              <Grid item>
                <Typography variant='button'>{translate('ra.message.no_registry')}</Typography>
              </Grid>
            )}

            <FormDivider show={true} text={translate('ra.field.products_inheritance')} />
            {record.product_by_work_unit_inheritance?.length ? (
              <>
                {record.product_by_work_unit_inheritance?.map((field, index) => (
                  <Grid container key={index}>
                    <ShowListElement
                      label={translate('ra.field.long_name')}
                      value={field.product_relation.long_name}
                    />
                    <ShowListElement
                      label={translate('ra.field.short_name')}
                      value={field.product_relation.short_name}
                    />
                    <ShowListElement
                      label={translate('ra.field.tma_code')}
                      value={field.product_relation.tma_code}
                    />
                    <ShowListElement label={translate('ra.field.token')} value={field.token_id} />
                    <ShowListElement
                      label={translate('ra.field.is_offline')}
                      value={field.product_relation.is_offline}
                      type='boolean'
                    />
                  </Grid>
                ))}
              </>
            ) : (
              <Grid item>
                <Typography variant='button'>{translate('ra.message.no_registry')}</Typography>
              </Grid>
            )}

            <FormDivider show={true} text={translate('ra.field.children')} />
            {record.children?.length ? (
              <>
                {record.children?.map((field, index) => (
                  <Grid container key={index}>
                    <ShowListElement label={translate('ra.field.token')} value={field.token} />
                    <ShowListElement label={translate('ra.field.long_name')} value={field.long_name} />
                    <ShowListElement label={translate('ra.field.short_name')} value={field.short_name} />
                    <ShowListElement label={translate('ra.field.description')} value={field.description} />
                    <ShowListElement
                      label={translate('ra.field.is_active')}
                      value={field.is_active}
                      type='boolean'
                    />
                    <FormDivider show={false} />
                  </Grid>
                ))}
              </>
            ) : (
              <Grid item>
                <Typography variant='button'>{translate('ra.message.no_registry')}</Typography>
              </Grid>
            )}
          </Grid>
        </CardCommon>
      </SimpleShowLayout>
      <HistoryBaseDialog open={openHistoryBase} onClose={handleCloseHistoryBase} record={record} />
    </>
  )
}

export const WorkUnitShow = (props) => {
  return (
    <Show component={ShowWrapper} {...props} className='CrudShowContainer' actions={null}>
      <ShowContent {...props} />
    </Show>
  )
}
